import { watch, onMounted } from 'vue'
import type { SEOData } from './useSEO'

interface OGData {
  title: string
  description: string
  image: string
  imageAlt: string
  type: 'website' | 'article' | 'product'
  url: string
  siteName: string
  locale: string
  publishedTime?: string
  modifiedTime?: string
  author?: string
  section?: string
  tags?: string[]
}

interface TwitterCardData {
  card: 'summary' | 'summary_large_image' | 'app' | 'player'
  site: string
  creator: string
  title: string
  description: string
  image: string
  imageAlt: string
}

const DEFAULT_SITE_NAME = 'Rifas La Favorita'
const DEFAULT_TWITTER_HANDLE = '@rifaslafavorita'
const SITE_URL = 'https://www.lafavoritarifas.com'

function updateMetaTag(property: string, content: string) {
  if (!content) return
  
  let tag = document.querySelector(`meta[property="${property}"]`) || 
            document.querySelector(`meta[name="${property}"]`)
  
  if (tag) {
    tag.setAttribute('content', content)
  } else {
    tag = document.createElement('meta')
    tag.setAttribute('property', property)
    tag.setAttribute('content', content)
    document.head.appendChild(tag)
  }
}

export function useOpenGraph() {
  
  const updateOG = (data: OGData) => {
    const {
      title,
      description,
      image,
      imageAlt,
      type = 'website',
      url = SITE_URL,
      siteName = DEFAULT_SITE_NAME,
      locale = 'es_VE',
      publishedTime,
      modifiedTime,
      author,
      section,
      tags
    } = data
    
    // Basic Open Graph
    updateMetaTag('og:title', title)
    updateMetaTag('og:description', description)
    updateMetaTag('og:image', image)
    updateMetaTag('og:image:alt', imageAlt)
    updateMetaTag('og:type', type)
    updateMetaTag('og:url', url)
    updateMetaTag('og:site_name', siteName)
    updateMetaTag('og:locale', locale)
    updateMetaTag('og:locale:alternate', 'es')
    
    // Optional Article properties
    if (type === 'article') {
      updateMetaTag('article:published_time', publishedTime)
      updateMetaTag('article:modified_time', modifiedTime)
      updateMetaTag('article:author', author)
      updateMetaTag('article:section', section)
      tags?.forEach((tag, index) => {
        updateMetaTag(`article:tag:${index + 1}`, tag)
      })
    }
    
    // Product properties
    if (type === 'product') {
      updateMetaTag('product:availability', 'in stock')
      updateMetaTag('product:condition', 'new')
    }
    
    // Image dimensions (optional, recommended)
    updateMetaTag('og:image:width', '1200')
    updateMetaTag('og:image:height', '630')
  }
  
  const updateTwitterCard = (data: TwitterCardData) => {
    const {
      card = 'summary_large_image',
      site = DEFAULT_TWITTER_HANDLE,
      creator = DEFAULT_TWITTER_HANDLE,
      title,
      description,
      image,
      imageAlt
    } = data
    
    updateMetaTag('twitter:card', card)
    updateMetaTag('twitter:site', site)
    updateMetaTag('twitter:creator', creator)
    updateMetaTag('twitter:title', title)
    updateMetaTag('twitter:description', description)
    updateMetaTag('twitter:image', image)
    updateMetaTag('twitter:image:alt', imageAlt)
  }
  
  const updateSocialMedia = (seoData: SEOData, additionalOG?: Partial<OGData>, additionalTwitter?: Partial<TwitterCardData>) => {
    const {
      title = DEFAULT_SITE_NAME,
      description = '',
      ogImage = `${SITE_URL}/og-image.png`,
      ogType = 'website',
      ogUrl = SITE_URL,
      twitterCard = 'summary_large_image'
    } = seoData
    
    updateOG({
      title,
      description,
      image: ogImage,
      imageAlt: title,
      type: ogType as 'website' | 'article' | 'product',
      url: ogUrl,
      ...additionalOG
    })
    
    updateTwitterCard({
      card: twitterCard as 'summary' | 'summary_large_image',
      title,
      description,
      image: ogImage,
      imageAlt: title,
      ...additionalTwitter
    })
  }
  
  const updateRaffleSocial = (raffle: any, isArticle = false) => {
    if (!raffle) return
    
    const title = `${raffle.title || 'Sorteo Exclusivo'} - Rifas La Favorita`
    const description = raffle.description || 
      `Participa en el sorteo de ${raffle.title}. Tickets desde $${raffle.ticket_price || '4'}. ¡Gana premios increíbles!`
    const image = raffle.image_url || raffle.prize_image || `${SITE_URL}/og-image.png`
    const url = `${SITE_URL}/sorteo/${raffle.id}`
    
    updateSocialMedia(
      { title, description, ogImage: image, ogUrl: url },
      {
        type: isArticle ? 'article' : 'product',
        publishedTime: raffle.created_at,
        modifiedTime: raffle.updated_at,
        tags: ['rifas', 'sorteos', 'premios', raffle.prize_name || 'sorteo']
      },
      {
        card: 'summary_large_image'
      }
    )
  }
  
  return {
    updateOG,
    updateTwitterCard,
    updateSocialMedia,
    updateRaffleSocial
  }
}
